import { useMutation } from '@tanstack/react-query';
import constate from 'constate';
import mime from 'mime';
import { useRef, useState } from 'react';

import { api, isDefined, parseError, useLoggedInAuthContext, useToast } from 'common';

interface useUploadKnowledgeBaseFileProps {
  onFileUploaded: (knowledgeBaseFile: api.KnowledgeBaseFile.KnowledgeBaseFile) => Promise<void>;
  flowOrganizationId?: string;
}

type OrganizationContext = (
  | {
      presetOrganizationId: string;
      canUpdateOrganizationId: false;
    }
  | {
      presetOrganizationId?: string;
      canUpdateOrganizationId: true;
    }
) & {
  organizationsAreVisible: boolean;
  defaultOrganizationId?: string;
  setPresetOrganizationId: (organizationId?: string) => void;
};

function useUploadKnowledgeBaseFile(props: useUploadKnowledgeBaseFileProps) {
  const { onFileUploaded, flowOrganizationId } = props;
  const [file, _setFile] = useState<File>();
  const inputFilesRef = useRef<Map<string, HTMLInputElement>>(new Map());
  const { showSuccess, showError } = useToast();

  const { isAdminRole, hasMultipleOrganizations, defaultOrganization } = useLoggedInAuthContext();
  const [presetOrganizationId, setPresetOrganizationId] = useState<string | undefined>(
    flowOrganizationId ?? defaultOrganization?.id
  );
  const organizationsAreVisible = isAdminRole || hasMultipleOrganizations;

  const setFile = (file: File | undefined) => {
    if (isDefined(file)) {
      _setFile(file);
      return;
    }
    inputFilesRef.current.forEach((input) => {
      if (input) {
        input.value = '';
      }
    });
    _setFile(undefined);
  };

  const uploadKnowledgeBaseFile = async (args: {
    file: File;
    name: string;
    organizationId: string;
  }) => {
    const { file, name, organizationId } = args;
    const contentType = mime.getType(file.name);
    if (!isDefined(contentType)) {
      throw new Error(`Cannot determine content type for file ${file.name}`);
    }

    const fileType = mime.getExtension(contentType);
    if (!isDefined(fileType)) {
      throw new Error(`Cannot determine file type for file ${file.name}`);
    }
    const knowledgeBaseFile = await api.client.KnowledgeBaseFile.create({
      name,
      organizationId,
      displayedType: fileType,
      dataSizeBytes: file.size,
    });
    await api.client.KnowledgeBaseFile.upload({
      file,
      knowledgeBaseFileId: knowledgeBaseFile.id,
      contentType,
      progressHandler: () => {},
    });
    return knowledgeBaseFile;
  };

  const { mutateAsync: uploadFile, isPending } = useMutation({
    mutationFn: async (values: { organizationId: string; userDefinedName: string }) => {
      if (!isDefined(file)) {
        throw new Error('No file selected');
      }
      return await uploadKnowledgeBaseFile({
        file,
        name: values.userDefinedName,
        organizationId: values.organizationId,
      });
    },
    onSuccess: async (uploadedFile, values) => {
      showSuccess(`File ${values.userDefinedName} uploaded successfully`);
      await onFileUploaded(uploadedFile);
    },
    onError: (error, values) => {
      showError(`Error uploading file ${values.userDefinedName}: ${parseError(error)}`);
    },
  });

  const registerInputFileRef = (id: string, ref: HTMLInputElement | null) => {
    if (!isDefined(ref)) {
      inputFilesRef.current.delete(id);
      return;
    }
    inputFilesRef.current.set(id, ref);
  };

  const organizationDetails: OrganizationContext = {
    organizationsAreVisible,
    defaultOrganizationId: defaultOrganization?.id,
    setPresetOrganizationId,
    ...(isDefined(presetOrganizationId)
      ? {
          presetOrganizationId,
          canUpdateOrganizationId: false,
        }
      : {
          presetOrganizationId: undefined,
          canUpdateOrganizationId: true,
        }),
  };

  return {
    file,
    setFile,
    uploadFile,
    isPending,
    ...organizationDetails,
    registerInputFileRef,
  };
}

export const [UploadFileProvider, useUploadFileContext] = constate(useUploadKnowledgeBaseFile);
